<template>
  <ion-header  class="resource-viewer-toolbar notes-list-modal notes-list-header" :class="direction">
    <ion-toolbar class="modal-toolbar notelist-modal-toolbar" mode="md">
      <div class="toolbar-actions" :class="direction">
        <div @click="close" class="close-btn" :class="direction" style="background-color: transparent!important;">
          <ion-icon :icon="closeSharp" />
        </div>
      </div>
    </ion-toolbar>
  </ion-header>

  <ion-content :scroll-y="false" class="notes-content" :class="direction">
    <div class="notes-list-container" :class="direction">
      <div class="notes-header">
        <ion-icon :icon="pencilOutline" class="notes-header-icon" />
        <h2 class="notes-title">{{ notesTitle }}</h2>
      </div>

      <div class="notes-actions" :class="direction">
        <ion-searchbar class="notes-search" :class="direction" v-model="query" :placeholder="searchPlaceholder" :debounce="0" @ionInput="refresh" />
        <div class="sub-menu-chiplet notes-add-chiplet" :class="direction" @click="addNote" title="Add note">
          <ion-icon class="chiplet-icon" :icon="addSharp" mode="md" />
        </div>
      </div>

      <div class="notes-scroll-area">
        <ion-card class="journey-card note-card" v-for="n in notes" :key="n.id" @click="openNote(n)">
          <div class="note-row" :class="direction">
            <div class="note-left" :class="direction">
              <ion-icon v-if="icons[n.note_metadata.lessonIconId]" :src="icons[n.note_metadata.lessonIconId]" class="note-list-icon" />
              <ion-icon v-else :icon="pencilOutline" class="note-list-icon generic" />
            </div>
            <div class="note-content" :class="direction">
              <h2 class="note-title" :class="direction">{{ n.note_metadata?.note_title || '(Untitled)' }}</h2>
              <p class="note-preview" :class="direction">{{ firstLine(n.note_body) }}</p>
              <p class="note-lesson" :class="direction">{{ n.note_metadata.lessonTitle }}</p>
            </div>
            <div v-if="hasAudioResources(n)" class="note-audio-icon" :class="direction">
              <ion-icon :icon="volumeHigh" />
            </div>
          </div>
        </ion-card>
      </div>
    </div>
  </ion-content>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { IonHeader, IonToolbar,  IonContent, IonSearchbar, IonIcon, IonCard, modalController } from '@ionic/vue';
import { addSharp, documentTextOutline, closeSharp, pencilOutline, volumeHigh } from 'ionicons/icons';
import { Note, createNewNote, getAllNotes, searchNotes } from '@/services/NotesService';
import NotesModal from '@/components/NotesModal.vue';
import { getMediaItem } from '@/storage/DataStorage';
import { getImageDataUri } from '@/utilities/Compatibility';
import { EMPTY_GUID } from '@/constants/app';
import { useStore } from 'vuex';
import translate from '@/views/translate';

export default defineComponent({
  name: 'NotesListModal',
  components: { IonHeader, IonToolbar,  IonContent, IonSearchbar, IonIcon, IonCard },
  setup() {
    const store = useStore();
    const { t } = translate();
    const localeMessages = computed(() => store.getters.getLocaleMessages());
    const direction = computed(() => t("messages.language.textDirection"));
    const notesTitle = computed(() => localeMessages.value?.application?.notesTitle || 'Notes');
    const searchPlaceholder = computed(() => localeMessages.value?.application?.searchPlaceholder || 'Search notes');
    const notes = ref<Note[]>([]);
    const query = ref('');
    const icons = ref<Record<string, string>>({});

    const loadIcons = async (items: Note[]) => {
      const toLoad = items
        .map(n => n.note_metadata.lessonIconId)
        .filter(id => id && id !== EMPTY_GUID && !icons.value[id]);
      for (const id of toLoad) {
        try {
          const media = await getMediaItem(id);
          icons.value[id] = getImageDataUri(media.content);
        } catch {
          // ignore
        }
      }
    };

    const refresh = async (ev?: any) => {
      const newVal = (ev && (typeof ev.target?.value === 'string' ? ev.target.value : typeof ev.detail?.value === 'string' ? ev.detail.value : undefined)) ?? query.value;
      query.value = (newVal || '').toString();
      const term = query.value.trim();
      const list = term ? await searchNotes(term) : await getAllNotes();
      notes.value = list;
      await loadIcons(list);
    };

    const openNote = async (n: Note) => {
      const modal = await modalController.create({
        component: NotesModal,
        cssClass: 'custom-modal-pdfviewer',
        componentProps: {
          dismiss: async () => await modalController.dismiss(),
          initialNote: n
        }
      });
      await modal.present();
      await modal.onDidDismiss();
      await refresh();
    };

    const addNote = async () => {
      await openNote(createNewNote());
    };

    const firstLine = (text: string) => {
      const line = (text || '').split('\n')[0];
      return line.length > 32 ? line.substring(0, 32) + '...' : line;
    };

    const hasAudioResources = (note: Note) => {
      const resources = note.note_metadata.audioResources || [];
      const legacyIds = note.note_metadata.audioResourceIds || [];
      return resources.length > 0 || legacyIds.length > 0;
    };

    const close = async () => { await modalController.dismiss(); };

    onMounted(refresh);
    return { notes, query, refresh, openNote, addNote, firstLine, hasAudioResources, addSharp, documentTextOutline, icons, closeSharp, close, pencilOutline, volumeHigh, notesTitle, searchPlaceholder, direction };
  }
});
</script>

<style scoped>
.modal-toolbar {
  --background: var(--color-app-background-rgb);
  padding-top: 0;
}
.toolbar-actions {
  position: absolute;
  right: 4px;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
}
.toolbar-actions.rtl {
  right: auto !important;
  left: 4px !important;
}
.close-btn {
  font-size: 22px;
  margin-right: 10px;
}
ion-button->button {
  background-color: transparent!important;
}
.notes-content {
  --padding-top: 0;
  --padding-bottom: 0;
  --padding-start: 0;
  --padding-end: 0;
}
.notes-list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.notes-list-header {
  height: 56px!important
}
.notelist-modal-toolbar {
  padding-top: 0;
}
.notes-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 8px;
  flex-shrink: 0;
}
.notes-header-icon {
  font-size: 90px;
  color: var(--color-primary);
}
.notes-title {
  margin: 8px 0 12px 0;
  width: 100%;
  text-align: center;
}
.notes-actions {
  display: flex;
  align-items: center;
  padding: 0 12px 6px 12px;
  --notes-search-height: 42px;
  --notes-chiplet-size: calc(var(--notes-search-height) - 4px);
  --notes-search-background: var(--color-card-background);
  flex-shrink: 0;
}
.notes-scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}
.note-list-icon {
  font-size: 45px;
  color:var(--color-primary);
}
ion-card.journey-card {
  --ion-background-color: var(--color-card-background);
  margin: 4px auto 8px auto;
  padding: 0px;
  width:94%;
  border-radius: 12px;
}
.note-row {
  display: flex;
  align-items: center;
  padding: 0px;
}
 


.note-row.rtl .note-left {
  order: 1 !important;
  margin-right: 0 !important;
  margin-left: 10px !important;
  border-radius: 0 12px 12px 0 !important;
}

.note-row.rtl .note-content {
  order: 2 !important;
}

.note-row.rtl .note-audio-icon {
  order: 0 !important;
}

.note-left {
  width: 60px;
  align-self: stretch;
  border-radius: 12px 0 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  min-width: 20%;
}
.note-content {
  flex: 1;
}
.note-audio-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  flex-shrink: 0;
}
.note-audio-icon ion-icon {
  font-size: 31.5px;
  color: rgb(100,100,100);
}
.note-list-icon.generic {
  color:var(--color-primary);
}
h2.note-title {
  color: var(--ion-color-custom-step-250);
  font-weight: bold;
  font-size: 18px;
  line-height: 18px!important;
  margin-top: 8px!important;
  margin-bottom: 0px!important;
  margin-right: 10px!important;
  overflow: hidden;
}
.note-preview {
  color: var(--ion-color-custom-step-250);
  font-size: 14px;
  line-height: 14px!important;
  margin-top: 8px!important;
  margin-bottom: 0px!important;
  margin-right: 10px!important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
}
.note-lesson {
  opacity: .7;
  font-size: 14px;
  line-height: 14px!important;
  margin-top: 8px!important;
  margin-bottom: 8px!important;
  margin-right: 10px!important;
}
.notes-search {
  flex: 1;
  --height: var(--notes-search-height);
  --border-radius: 12px;
}
.notes-add-chiplet {
  margin-left: 8px;
  height: var(--notes-chiplet-size);
  width: var(--notes-chiplet-size);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background-color: var(--color-card-background);

}

.notes-add-chiplet ion-icon {
  margin-top:-4px;
  margin-left: 2px;
}

.notes-add-chiplet .chiplet-icon {
  font-size: 32px;
}

/* RTL Support */
.close-btn.rtl {
  margin-right: 0 !important;
  margin-left: 10px !important;
}

.notes-header.rtl {
  text-align: right;
}

.notes-title.rtl {
  text-align: center;
  direction: rtl;
}


.note-content.rtl {
  text-align: right;
}

.note-title.rtl {
  text-align: right;
}

.note-preview.rtl {
  text-align: right;
}

.note-lesson.rtl {
  text-align: right;
}

.notes-search.rtl {
  direction: rtl;
}

.notes-search.rtl input {
  text-align: right;
}
</style>


